import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useFeature } from '../context/AuthContext';
import { apiGet, apiPatch, apiPost } from '../lib/api';
import { useRefs } from '../context/RefsContext';

interface ClientUser {
  id_user: number;
  nom_user: string;
  mail: string;
  traitant: 0 | 1;
  self_only: 0 | 1;
}

type Form = Record<string, string>;

const EMPTY: Form = {
  nom_client: '', nom_contact: '', adresse: '', adresse2: '', cp: '', ville: '', pays: '',
  email: '', email_relances: '', telephone: '', email_urgence: '', telephone_urgence: '',
  contrat_rgdp_recu: 'non', contrat_rgdp_recu_2018: 'non', acompte: '100', taux_tva_usuel: '20',
  nb_jour_mois: '0', aff_stats: '1', mode_tma: 'credit', limitation_points: '0',
  ouverture_we: 'non', montant_credit_temps: '110.00', credit_temps_courant: '0',
  conso_mois_courant: '0', increment_credit_mensuel: '0', client_compta_pcg: '',
};

export function AdminClientEditPage() {
  const canWrite = useFeature('admin_accounts');
  const { id } = useParams();
  const isNew = !id || id === 'new';
  const navigate = useNavigate();

  const [form, setForm] = useState<Form>(EMPTY);
  const [linkUserIds, setLinkUserIds] = useState<number[]>([]);
  const [users, setUsers] = useState<ClientUser[]>([]);
  const [loading, setLoading] = useState(!isNew);
  const [msg, setMsg] = useState<{ ok?: string; err?: string } | null>(null);
  const [saving, setSaving] = useState(false);

  const set = (k: string) => (v: string) => setForm((f) => ({ ...f, [k]: v }));

  const loadUsers = () => {
    if (isNew) return;
    apiGet<{ client: Record<string, unknown>; users: ClientUser[] }>(`/api/admin/clients/${id}`)
      .then((d) => setUsers(d.users))
      .catch(() => {});
  };

  useEffect(() => {
    if (isNew) return;
    apiGet<{ client: Record<string, unknown>; users: ClientUser[] }>(`/api/admin/clients/${id}`)
      .then((d) => {
        const f: Form = { ...EMPTY };
        for (const k of Object.keys(EMPTY)) if (d.client[k] != null) f[k] = String(d.client[k]);
        setForm(f);
        setLinkUserIds(Array.isArray(d.client.link_user_ids) ? (d.client.link_user_ids as number[]) : []);
        setUsers(d.users);
      })
      .catch((e) => setMsg({ err: e instanceof Error ? e.message : 'Erreur' }))
      .finally(() => setLoading(false));
  }, [id, isNew]);

  const save = async () => {
    setMsg(null);
    if (!(form.nom_client ?? '').trim()) return setMsg({ err: 'Le nom du client est requis' });
    setSaving(true);
    try {
      if (isNew) {
        const r = await apiPost<{ id_client: number }>('/api/admin/clients', form);
        navigate(`/admin/clients/${r.id_client}`);
      } else {
        await apiPatch(`/api/admin/clients/${id}`, form);
        setMsg({ ok: 'Client enregistré' });
      }
    } catch (e) {
      setMsg({ err: e instanceof Error ? e.message : 'Erreur' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div style={{ padding: 24, color: 'var(--text-mute)' }}>Chargement…</div>;

  const isCredit = form.mode_tma === 'credit';

  return (
    <div style={{ maxWidth: 860 }}>
      <Link to="/admin/clients" style={{ fontSize: 'var(--fs-xs)' }}>
        ← liste des clients
      </Link>
      <h1 style={{ fontSize: 'var(--fs-xl)', fontWeight: 600, margin: '4px 0 16px' }}>
        {isNew ? 'Nouveau client' : form.nom_client || 'Client'}
      </h1>

      <div className="grid-2" style={{ gap: 16 }}>
        <Card title="Coordonnées">
          <Field label="Nom du client *" value={form.nom_client} onChange={set('nom_client')} />
          <Field label="Contact" value={form.nom_contact} onChange={set('nom_contact')} />
          <Field label="Adresse" value={form.adresse} onChange={set('adresse')} />
          <Field label="Complément adresse" value={form.adresse2} onChange={set('adresse2')} />
          <Row>
            <Field label="Code postal" value={form.cp} onChange={set('cp')} />
            <Field label="Ville" value={form.ville} onChange={set('ville')} />
          </Row>
          <Field label="Pays" value={form.pays} onChange={set('pays')} />
          <Field label="Email commercial" value={form.email} onChange={set('email')} />
          <Field label="Email relances factures" value={form.email_relances} onChange={set('email_relances')} />
          <Field label="Téléphone" value={form.telephone} onChange={set('telephone')} />
          <Field label="Email urgence" value={form.email_urgence} onChange={set('email_urgence')} />
          <Field label="Téléphone urgence" value={form.telephone_urgence} onChange={set('telephone_urgence')} />
          <Row>
            <Select label="Contrat RGPD reçu" value={form.contrat_rgdp_recu} onChange={set('contrat_rgdp_recu')} options={['non', 'oui']} />
            <Select label="Contrat RGPD 2018" value={form.contrat_rgdp_recu_2018} onChange={set('contrat_rgdp_recu_2018')} options={['non', 'oui']} />
          </Row>
        </Card>

        <Card title="Crédit / TMA / Facturation">
          <Select label="Mode" value={form.mode_tma} onChange={set('mode_tma')} options={['forfait', 'credit']} />
          <Select label="Montant horaire (€)" value={form.montant_credit_temps} onChange={set('montant_credit_temps')} options={['85.00', '110.00']} />
          <Row>
            <Field label="Crédit courant (min)" value={form.credit_temps_courant} onChange={set('credit_temps_courant')} type="number" disabled={!isCredit} />
            <Field label="Conso mois (min)" value={form.conso_mois_courant} onChange={set('conso_mois_courant')} type="number" disabled={!isCredit} />
          </Row>
          <Field label="Incrément crédit mensuel (min)" value={form.increment_credit_mensuel} onChange={set('increment_credit_mensuel')} type="number" disabled={!isCredit} />
          <Row>
            <Field label="Taux TVA (%)" value={form.taux_tva_usuel} onChange={set('taux_tva_usuel')} type="number" />
            <Field label="Jours TMA / mois" value={form.nb_jour_mois} onChange={set('nb_jour_mois')} type="number" />
          </Row>
          <Row>
            <Select label="Affichage stats" value={form.aff_stats} onChange={set('aff_stats')} options={['0', '1']} />
            <Select label="Ouverture WE" value={form.ouverture_we} onChange={set('ouverture_we')} options={['non', 'oui']} />
          </Row>
          <Row>
            <Field label="Acompte usuel" value={form.acompte} onChange={set('acompte')} type="number" />
            <Field label="Limitation points" value={form.limitation_points} onChange={set('limitation_points')} type="number" />
          </Row>
          <Field label="Compte compta (PCG)" value={form.client_compta_pcg} onChange={set('client_compta_pcg')} />
        </Card>
      </div>

      {msg?.err && <FormMsg err>{msg.err}</FormMsg>}
      {msg?.ok && <FormMsg>{msg.ok}</FormMsg>}

      <div style={{ marginTop: 12 }}>
        <button className="btn primary" onClick={save} disabled={saving || !canWrite}>
          {saving ? 'Enregistrement…' : isNew ? 'Créer le client' : 'Enregistrer'}
        </button>
      </div>

      {!isNew && <DefaultTraitantsCard clientId={Number(id)} initial={linkUserIds} />}

      {!isNew && (
        <div style={{ marginTop: 24 }}>
          <h2 style={{ fontSize: 'var(--fs-md)', fontWeight: 600, marginBottom: 12 }}>Utilisateurs</h2>
          {users.map((u) => (
            <UserRow key={u.id_user} user={u} onSaved={loadUsers} />
          ))}
          {users.length === 0 && (
            <div style={{ color: 'var(--text-mute)', fontSize: 'var(--fs-sm)', marginBottom: 12 }}>Aucun utilisateur.</div>
          )}
          <AddUserForm clientId={Number(id)} onAdded={loadUsers} />
        </div>
      )}
    </div>
  );
}

// ---- Traitants par défaut (client.link_user) ---------------------------------
// Liste à choix multiple, comme la fiche client du V1 (table-lib.cgi
// l.484-507), restreinte aux traitants Digital Team. Enregistrée à part
// (PATCH …/traitants, interrupteur client_traitants) au format du V1 `,a,b,`.
// Retour 16 du rapport n°33 (PS 11323).

function DefaultTraitantsCard({ clientId, initial }: { clientId: number; initial: number[] }) {
  const canWrite = useFeature('client_traitants');
  const { traitants } = useRefs();
  const sorted = [...traitants].sort((a, b) => a.nom_user.localeCompare(b.nom_user));
  const [ids, setIds] = useState<number[]>(initial);
  const [msg, setMsg] = useState<{ ok?: string; err?: string } | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => setIds(initial), [initial]);
  // Identifiants présents dans link_user mais absents de la liste des traitants
  // Digital Team (saisie libre d'avant, compte d'un autre client) : retirés à
  // l'enregistrement, on le dit avant.
  const unknown = ids.filter((i) => !traitants.some((t) => t.id_user === i));

  const toggle = (id: number) => setIds((cur) => (cur.includes(id) ? cur.filter((n) => n !== id) : [...cur, id]));

  const save = async () => {
    setMsg(null);
    setBusy(true);
    try {
      const r = await apiPatch<{ ids: number[] }>(`/api/admin/clients/${clientId}/traitants`, {
        ids: ids.filter((i) => traitants.some((t) => t.id_user === i)),
      });
      setIds(r.ids);
      setMsg({ ok: 'Traitants par défaut enregistrés' });
    } catch (e) {
      setMsg({ err: e instanceof Error ? e.message : 'Erreur' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ marginTop: 24 }}>
      <Card title="Traitants par défaut">
        <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-mute)', marginBottom: 8 }}>
          Traitants d’un nouveau point de ce client (remplacés par leur substitut s’ils sont en congé). Aucun coché : le point va
          à l’utilisateur 3, comme dans le V1.
        </div>
        <div className="grid-2" style={{ gap: 6 }}>
          {sorted.map((t) => (
            <label key={t.id_user} style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 'var(--fs-xs)' }}>
              <input type="checkbox" checked={ids.includes(t.id_user)} onChange={() => toggle(t.id_user)} disabled={!canWrite} />
              {t.nom_user}
            </label>
          ))}
        </div>
        {unknown.length > 0 && (
          <FormMsg err>
            Hors liste des traitants Digital Team, retiré(s) à l’enregistrement : {unknown.map((i) => `#${i}`).join(', ')}
          </FormMsg>
        )}
        {msg?.err && <FormMsg err>{msg.err}</FormMsg>}
        {msg?.ok && <FormMsg>{msg.ok}</FormMsg>}
        <div style={{ marginTop: 12 }}>
          <button className="btn primary" onClick={save} disabled={busy || !canWrite}>
            {busy ? 'Enregistrement…' : 'Enregistrer les traitants'}
          </button>
        </div>
      </Card>
    </div>
  );
}

// ---- Ligne utilisateur (édition inline) -------------------------------------

function UserRow({ user, onSaved }: { user: ClientUser; onSaved: () => void }) {
  const canWrite = useFeature('admin_accounts');
  const [nom, setNom] = useState(user.nom_user);
  const [mail, setMail] = useState(user.mail);
  const [selfOnly, setSelfOnly] = useState(user.self_only === 1);
  const [msg, setMsg] = useState<{ ok?: string; err?: string } | null>(null);
  const [busy, setBusy] = useState(false);

  const save = async (extra?: Record<string, unknown>) => {
    setMsg(null);
    setBusy(true);
    try {
      await apiPatch(`/api/admin/users/${user.id_user}`, {
        nom_user: nom,
        mail,
        self_only: selfOnly ? 1 : 0,
        ...extra,
      });
      setMsg({ ok: 'Enregistré' });
      onSaved();
    } catch (e) {
      setMsg({ err: e instanceof Error ? e.message : 'Erreur' });
    } finally {
      setBusy(false);
    }
  };

  const resetPwd = async () => {
    const pwd = window.prompt('Nouveau mot de passe (8 caractères min. : lettres sans accent, chiffres et @ . -) :');
    if (!pwd) return;
    if (pwd.length < 8) return setMsg({ err: 'Au moins 8 caractères' });
    await save({ password: pwd });
  };

  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: 8, padding: 12, background: 'var(--bg)', marginBottom: 8 }}>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div style={{ flex: '1 1 160px' }}>
          <Field label="Nom" value={nom} onChange={setNom} />
        </div>
        <div style={{ flex: '1 1 200px' }}>
          <Field label="Email" value={mail} onChange={setMail} />
        </div>
        {/* Statut traitant : en lecture seule, il ne se change pas par la V2
            (l'API refuse le champ en écriture). */}
        {user.traitant === 1 && (
          <span style={{ fontSize: 'var(--fs-xs)', paddingBottom: 8, color: 'var(--text-mute)' }} title="Statut traitant (lecture seule)">
            Traitant
          </span>
        )}
        <label style={{ fontSize: 'var(--fs-xs)', display: 'flex', alignItems: 'center', gap: 4, paddingBottom: 8 }}>
          <input type="checkbox" checked={selfOnly} onChange={(e) => setSelfOnly(e.target.checked)} /> Restreint
        </label>
        <button className="btn" onClick={() => save()} disabled={busy || !canWrite} style={{ fontSize: 'var(--fs-xs)' }}>
          Enregistrer
        </button>
        <button className="btn ghost" onClick={resetPwd} disabled={busy || !canWrite} style={{ fontSize: 'var(--fs-xs)' }}>
          Réinit. mdp
        </button>
      </div>
      {msg?.err && <FormMsg err>{msg.err}</FormMsg>}
      {msg?.ok && <FormMsg>{msg.ok}</FormMsg>}
    </div>
  );
}

// ---- Ajout d'un utilisateur --------------------------------------------------

function AddUserForm({ clientId, onAdded }: { clientId: number; onAdded: () => void }) {
  const canWrite = useFeature('admin_accounts');
  const [nom, setNom] = useState('');
  const [mail, setMail] = useState('');
  const [pwd, setPwd] = useState('');
  const [selfOnly, setSelfOnly] = useState(false);
  const [msg, setMsg] = useState<{ ok?: string; err?: string } | null>(null);
  const [busy, setBusy] = useState(false);

  const add = async () => {
    setMsg(null);
    if (!nom.trim() || !mail.trim()) return setMsg({ err: 'Nom et email requis' });
    if (pwd.length < 8) return setMsg({ err: 'Mot de passe : 8 caractères min.' });
    setBusy(true);
    try {
      await apiPost(`/api/admin/clients/${clientId}/users`, {
        nom_user: nom,
        mail,
        password: pwd,
        self_only: selfOnly ? 1 : 0,
      });
      setNom('');
      setMail('');
      setPwd('');
      setSelfOnly(false);
      setMsg({ ok: 'Utilisateur créé' });
      onAdded();
    } catch (e) {
      setMsg({ err: e instanceof Error ? e.message : 'Erreur' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ border: '1px dashed var(--border-strong)', borderRadius: 8, padding: 12, marginTop: 8 }}>
      <div style={{ fontSize: 'var(--fs-xs)', fontWeight: 600, marginBottom: 8, color: 'var(--text-mute)' }}>
        Ajouter un utilisateur
      </div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div style={{ flex: '1 1 150px' }}>
          <Field label="Nom" value={nom} onChange={setNom} />
        </div>
        <div style={{ flex: '1 1 180px' }}>
          <Field label="Email" value={mail} onChange={setMail} />
        </div>
        <div style={{ flex: '1 1 140px' }}>
          <Field label="Mot de passe" value={pwd} onChange={setPwd} type="password" />
        </div>
        <label style={{ fontSize: 'var(--fs-xs)', display: 'flex', alignItems: 'center', gap: 4, paddingBottom: 8 }}>
          <input type="checkbox" checked={selfOnly} onChange={(e) => setSelfOnly(e.target.checked)} /> Restreint
        </label>
        <button className="btn primary" onClick={add} disabled={busy || !canWrite} style={{ fontSize: 'var(--fs-xs)' }}>
          Ajouter
        </button>
      </div>
      {msg?.err && <FormMsg err>{msg.err}</FormMsg>}
      {msg?.ok && <FormMsg>{msg.ok}</FormMsg>}
    </div>
  );
}

// ---- UI helpers --------------------------------------------------------------

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: 8, padding: 16, background: 'var(--bg)' }}>
      <h3 style={{ margin: '0 0 12px', fontSize: 'var(--fs-base)', fontWeight: 600 }}>{title}</h3>
      {children}
    </div>
  );
}

function Row({ children }: { children: React.ReactNode }) {
  return <div style={{ display: 'flex', gap: 8 }}>{children}</div>;
}

function Field({
  label,
  value,
  onChange,
  type = 'text',
  disabled,
}: {
  label: string;
  value: string | undefined;
  onChange: (v: string) => void;
  type?: string;
  disabled?: boolean;
}) {
  return (
    <div style={{ marginBottom: 8, flex: 1 }}>
      <label style={{ display: 'block', fontSize: 'var(--fs-xs)', color: 'var(--text-mute)', marginBottom: 2 }}>{label}</label>
      <input
        type={type}
        value={value ?? ''}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        style={{
          width: '100%',
          padding: 8,
          border: '1px solid var(--border-strong)',
          borderRadius: 6,
          fontSize: 'var(--fs-sm)',
          background: disabled ? 'var(--bg-soft)' : 'var(--bg)',
          color: disabled ? 'var(--text-mute)' : 'inherit',
        }}
      />
    </div>
  );
}

function Select({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string | undefined;
  onChange: (v: string) => void;
  options: string[];
}) {
  return (
    <div style={{ marginBottom: 8, flex: 1 }}>
      <label style={{ display: 'block', fontSize: 'var(--fs-xs)', color: 'var(--text-mute)', marginBottom: 2 }}>{label}</label>
      <select
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
        style={{ width: '100%', padding: 8, border: '1px solid var(--border-strong)', borderRadius: 6, fontSize: 'var(--fs-sm)' }}
      >
        {options.map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </select>
    </div>
  );
}

function FormMsg({ children, err }: { children: React.ReactNode; err?: boolean }) {
  return (
    <div
      style={{
        marginTop: 8,
        padding: '6px 10px',
        background: err ? 'var(--danger-soft)' : 'var(--success-soft)',
        border: `1px solid ${err ? 'var(--danger-border)' : 'var(--success-border)'}`,
        borderRadius: 6,
        fontSize: 'var(--fs-xs)',
        color: err ? 'var(--danger)' : 'var(--success)',
      }}
    >
      {children}
    </div>
  );
}
